import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import {
  PLAuthError,
  PlAuthService,
  PLAuthSuccess,
  selectAuthState,
} from 'pl-core-services';
import { environment } from '@environments/environment';
import { BehaviorSubject, filter, firstValueFrom, timeout } from 'rxjs';
import { take } from 'rxjs/operators';

interface RefreshChannelMessage extends AuthChannelMessageBase {
  type: 'REFRESH_INIT' | 'REFRESH_DONE';
  token?: string;
  oidcState?: string;
  userData?: any;
}

interface AuthChannelMessageBase {
  type:
    | 'REQUEST_AUTH_STATE'
    | 'AUTH_STATE'
    | 'REFRESH_INIT'
    | 'REFRESH_DONE'
    | 'LOGOUT_INIT';
  tabId: string;
  timestamp: number;
}

interface LogoutMessage extends AuthChannelMessageBase {
  type: 'LOGOUT_INIT';
}

interface RequestAuthStateMessage extends AuthChannelMessageBase {
  type: 'REQUEST_AUTH_STATE';
}

interface AuthStateMessage extends AuthChannelMessageBase {
  type: 'AUTH_STATE';
  token: string;
  userData: any;
  oidcState: string;
}

interface UserData {
  auth_db_user_uuid: string;
  auth_db_user_username: string;
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  name: string;
  preferred_username: string;
  role: string;
  sub: string;
}

interface OIDCState {
  access_token_expires_at: number | null;
  authNonce: string | null;
  authStateControl: string | null;
  authWellKnownEndPoints: any;
  authnResult: any;
  authzData: string;
  codeVerifier: string;
  jwtKeys: any;
  session_state: string;
  storageSilentRenewRunning: string;
  userData: UserData;
}

type AuthChannelMessage =
  | RequestAuthStateMessage
  | AuthStateMessage
  | RefreshChannelMessage
  | LogoutMessage;

const LOG_ERROR = 'ERROR';
const LOG_WARN = 'WARN';
const LOG_INFO = 'INFO';

@Injectable({ providedIn: 'root' })
export class AuthSyncService implements OnDestroy {
  private authChannel: BroadcastChannel;

  private leaderKey = 'pl_leader_key';

  private isLeader = false;

  private authWorker: Worker | null = null;

  private authStateReceived$ = new BehaviorSubject<boolean>(false);

  async waitForAuthState(timeoutMs: number = 2000): Promise<boolean> {
    try {
      await firstValueFrom(
        this.authStateReceived$.pipe(
          filter((received) => received),
          take(1),
          timeout(timeoutMs)
        )
      );
      return true;
    } catch {
      return false;
    }
  }

  async broadcastLogout() {
    this.log(LOG_INFO, 'Broadcasting logout to tabs');
    this.authChannel.postMessage({
      type: 'LOGOUT_INIT',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
  }

  private log(level: string, message: string, data?: any) {
    if (environment.debug) {
      switch (level) {
        case LOG_ERROR:
          // eslint-disable-next-line no-console
          console.error(`[AuthSyncService] ${message}`, data || '');
          break;
        case LOG_WARN:
          // eslint-disable-next-line no-console
          console.warn(`[AuthSyncService] ${message}`, data || '');
          break;
        case LOG_INFO:
          // eslint-disable-next-line no-console
          console.log(`[AuthSyncService] ${message}`, data || '');
      }
    }
  }

  isRefreshing = false;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private store: Store,
    private authService: PlAuthService,
    private http: HttpClient
  ) {
    this.log(LOG_INFO, 'Constructor called');

    this.store.select(selectAuthState).subscribe((authState) => {
      this.log(LOG_INFO, 'Auth store state updated:', authState);
    });

    this.authChannel = new BroadcastChannel('pl_auth_channel');
    this.log(LOG_INFO, `BroadcastChannel ${this.authChannel.name} created`);

    this.initWorker();
    this.setupChannelListeners();
    this.electLeader();

    window.addEventListener('storage', this.handleLeaderChange.bind(this));
    window.addEventListener('beforeunload', this.cleanupOnUnload.bind(this));

    this.requestAuthState(); // Request auth state when service initializes in new tab
  }

  private initWorker() {
    this.authWorker = new Worker('/assets/workers/auth-worker.js', {
      type: 'module',
    });

    this.log(LOG_INFO, 'Worker initialized and ready');

    this.authWorker.onmessage = (event) => {
      const { type, timestamp, error } = event.data;

      switch (type) {
        case 'HEARTBEAT':
          this.maintainLeadership(timestamp);
          break;

        case 'TOKEN_REFRESH_NEEDED':
          this.log(LOG_INFO, 'Token refresh needed');
          if (this.isLeader && !this.isRefreshing) {
            this.log(LOG_INFO, 'Leader tab will refresh token');
            this.handleTokenRefresh();
          }
          break;

        case 'AUTH_ERROR':
          this.log(LOG_ERROR, 'Auth error received from worker:', error);
          this.store.dispatch(PLAuthError({ error }));
          break;

        default:
          this.log(LOG_WARN, `Unhandled worker message type: ${type}`);
      }
    };
  }

  private handleLeaderChange(event: StorageEvent) {
    if (event.key === this.leaderKey) {
      if (event.newValue) {
        const { tabId, timestamp } = JSON.parse(event.newValue);

        // Ignore updates from the current leader
        if (this.isLeader && this.getTabId() === tabId) {
          return;
        }

        // ignore if the leader is still active
        const now = Date.now();
        if (now - timestamp <= 5000) {
          return;
        }

        this.log(
          LOG_WARN,
          `Leader tab ${tabId} is inactive. This tab is taking over.`
        );
        this.electLeader();
      } else {
        this.log(LOG_WARN, 'Leader key was removed. Electing new leader.');
        const randomDelay = Math.floor(Math.random() * 1000); // 0-100ms delay
        setTimeout(() => this.electLeader(), randomDelay);
      }
    }
  }

  private becomeLeader() {
    this.isLeader = true;

    localStorage.setItem(
      this.leaderKey,
      JSON.stringify({ tabId: this.getTabId(), timestamp: Date.now() })
    );

    this.authWorker?.postMessage({ type: 'BECOME_LEADER' });
  }

  private relinquishLeader() {
    this.isLeader = false;
    this.log(LOG_INFO, 'Relinquishing leadership');
    this.authWorker?.postMessage({ type: 'RELINQUISH_LEADER' });
  }

  private electLeader() {
    const existingLeader = localStorage.getItem(this.leaderKey);

    if (!existingLeader) {
      this.log(LOG_INFO, 'No leader exists, this tab becomes the leader');
      this.becomeLeader();
      return;
    }

    const { tabId, timestamp } = JSON.parse(existingLeader);

    // if the existing leader is inactive for too long, this tab takes over
    if (Date.now() - timestamp > 5000) {
      this.log(
        LOG_WARN,
        `Leader tab ${tabId} is inactive. This tab is taking over.`
      );
      this.becomeLeader();
    } else {
      this.log(
        LOG_INFO,
        `Leader exists: Tab ${tabId} is the current leader, last active ${
          Date.now() - timestamp
        }ms ago.`
      );
    }
  }

  private maintainLeadership(timestamp: number) {
    if (this.isLeader) {
      localStorage.setItem(
        this.leaderKey,
        JSON.stringify({ tabId: this.getTabId(), timestamp })
      );
    }
  }

  private cleanupOnUnload() {
    if (this.isLeader) {
      this.log(LOG_INFO, 'Tab is closing. Removing leader key.');
      localStorage.removeItem(this.leaderKey);
    }
    this.relinquishLeader();
  }

  private hasOidcState(
    message: AuthChannelMessage
  ): message is AuthStateMessage {
    return message.type === 'AUTH_STATE' && 'oidcState' in message;
  }

  private setupChannelListeners() {
    this.log(LOG_INFO, 'Setting up channel listeners');

    this.authChannel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      switch (event.data.type) {
        case 'REQUEST_AUTH_STATE':
          this.oidcSecurityService
            .getAccessToken()
            .pipe(take(1))
            .subscribe((token) => {
              if (token) {
                this.oidcSecurityService
                  .getUserData()
                  .pipe(take(1))
                  .subscribe((userData) => {
                    const oidcState = sessionStorage.getItem('kanga-local');
                    if (oidcState) {
                      this.authChannel.postMessage({
                        type: 'AUTH_STATE',
                        tabId: this.getTabId(),
                        timestamp: Date.now(),
                        token,
                        userData,
                        oidcState,
                      } as AuthStateMessage);
                    } else {
                      this.log(
                        LOG_WARN,
                        'No OIDC state found in session storage'
                      );
                    }
                  });
              } else {
                this.log(
                  LOG_WARN,
                  'No access token available to send AUTH_STATE'
                );
              }
            });
          break;

        case 'AUTH_STATE':
          if (this.hasOidcState(event.data)) {
            this.oidcSecurityService
              .getAccessToken()
              .pipe(take(1))
              .subscribe((currentToken) => {
                if (!currentToken) {
                  const data = event.data as AuthStateMessage;
                  sessionStorage.setItem('kanga-local', data.oidcState);
                  this.store.dispatch(
                    PLAuthSuccess({
                      token: data.token,
                      user: data.userData,
                    })
                  );
                  this.getUserAndCheckAuth(data.oidcState);
                  this.log(LOG_INFO, 'Got auth state from another tab');
                  // Signal that auth state was received
                  this.authStateReceived$.next(true);
                }
              });
          } else {
            this.log(
              LOG_WARN,
              'Received message is not AUTH_STATE',
              event.data
            );
          }
          break;

        case 'REFRESH_INIT':
          this.log(
            LOG_INFO,
            'Invoked REFRESH_INIT - another tab is handling the refresh'
          );
          this.isRefreshing = true;
          break;

        case 'LOGOUT_INIT':
          this.log(LOG_INFO, `Processing logout from tab ${event.data.tabId}`);
          this.handleLogoutMessage(event.data);
          break;

        case 'REFRESH_DONE': {
          this.log(
            LOG_INFO,
            'Invoked REFRESH_DONE - refresh completed by another tab, syncing'
          );
          this.isRefreshing = false;

          const { token, oidcState, userData } = event.data;
          if (token && oidcState && userData) {
            sessionStorage.setItem('kanga-local', oidcState);
            this.store.dispatch(PLAuthSuccess({ token, user: userData }));
            this.getUserAndCheckAuth(oidcState);
          } else {
            this.log(
              LOG_WARN,
              'REFRESH_DONE message is missing required fields'
            );
          }
          break;
        }
      }
    };
  }

  private requestAuthState() {
    this.log(LOG_INFO, 'Requesting auth state');

    // Reset the auth state received flag before requesting
    this.authStateReceived$.next(false);

    this.authChannel.postMessage({
      type: 'REQUEST_AUTH_STATE',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
  }

  private getTabId(): string {
    if (!sessionStorage.getItem('pl_tab_id')) {
      sessionStorage.setItem('pl_tab_id', crypto.randomUUID());
    }
    return sessionStorage.getItem('pl_tab_id')!;
  }

  notifyRefreshStart() {
    this.authChannel.postMessage({
      type: 'REFRESH_INIT',
      tabId: this.getTabId(),
      timestamp: Date.now(),
    });
  }

  notifyRefreshDone(token: string, oidcState: string, userData: any) {
    this.authChannel.postMessage({
      type: 'REFRESH_DONE',
      tabId: this.getTabId(),
      timestamp: Date.now(),
      token,
      oidcState,
      userData,
    });
  }

  startTokenMonitoring(token: string) {
    if (!this.authWorker) {
      this.log(LOG_WARN, 'No worker available for token monitoring');
      return;
    }

    this.oidcSecurityService
      .getConfiguration()
      .pipe(take(1))
      .subscribe((config) => {
        const refreshThreshold = config.renewTimeBeforeTokenExpiresInSeconds;

        this.log(
          LOG_INFO,
          'Starting token monitoring with refresh threshold:',
          refreshThreshold
        );
        this.authWorker.postMessage({
          type: 'START_TOKEN_MONITORING',
          data: {
            token,
            refreshThreshold,
          },
        });
      });
  }

  private cachedState: {
    oidcState?: string | null;
    refreshToken?: string | null;
  } = {};

  private preserveAuthState() {
    const oidcState = sessionStorage.getItem('kanga-local');

    this.oidcSecurityService
      .getRefreshToken()
      .pipe(take(1))
      .subscribe((refreshToken) => {
        this.cachedState = {
          oidcState,
          refreshToken,
        };

        this.log(LOG_INFO, 'Auth state preserved:', this.cachedState);
      });
  }

  private restoreAuthState(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.cachedState.oidcState || !this.cachedState.refreshToken) {
        this.log(LOG_WARN, 'No cached state to restore');
        resolve(false);
        return;
      }

      try {
        // Restore OIDC session state
        sessionStorage.setItem('kanga-local', this.cachedState.oidcState);

        // Update the auth config with restored refresh token
        this.oidcSecurityService
          .getConfiguration()
          .pipe(take(1))
          .subscribe({
            next: (config) => {
              if (config) {
                sessionStorage.setItem(
                  `${config.configId}_refresh_token`,
                  this.cachedState.refreshToken
                );
                this.log(LOG_INFO, 'Auth state restored successfully');
                resolve(true);
              } else {
                this.log(
                  LOG_WARN,
                  'Configuration not available for restoring state'
                );
                resolve(false);
              }
            },
            error: (error) => {
              this.log(LOG_ERROR, 'Error while restoring auth state:', error);
              resolve(false);
            },
          });
      } catch (e) {
        this.log(LOG_ERROR, 'Failed to restore auth state:', e);
        resolve(false);
      }
    });
  }

  private handleTokenRefresh(retryAttempt = 0) {
    if (this.isRefreshing && retryAttempt === 0) {
      this.log(LOG_INFO, 'Refresh already in progress, skipping');
      return;
    }

    // On first attempt, preserve current state
    if (retryAttempt === 0) {
      this.preserveAuthState();
    }

    this.log(LOG_INFO, `Starting token refresh attempt ${retryAttempt + 1}`);
    this.isRefreshing = true;
    this.notifyRefreshStart();

    const refreshStartTime = Date.now();

    // If state is missing but we have cached state, restore it
    this.oidcSecurityService.getRefreshToken().subscribe({
      next: (refreshToken) => {
        if (!refreshToken && this.cachedState.refreshToken) {
          this.log(LOG_WARN, 'No refresh token available, attempting restore');
          this.restoreAuthState().then((restored) => {
            if (!restored) {
              this.log(
                LOG_WARN,
                'Failed to restore auth state, proceeding with refresh'
              );
            }
          });
        }

        this.oidcSecurityService.forceRefreshSession().subscribe({
          next: (result) => {
            // NOTE: Should you need to inspect values here,
            // you can do so by checking for one or both of:
            // - `result?.accessToken`
            // - `this.oidcSecurityService.getRefreshToken()`
            // Then decoding them using the helper I've added
            // for inspecting tokens `this.decodeToken(token)`

            const token = result?.accessToken;
            if (token) {
              this.oidcSecurityService.getUserData().subscribe({
                next: (userData) => {
                  const oidcState = sessionStorage.getItem('kanga-local');

                  this.isRefreshing = false;
                  this.cachedState = {};

                  this.notifyRefreshDone(token, oidcState, userData);

                  this.refreshSession(token).then(
                    () => {
                      this.log(
                        LOG_INFO,
                        'Session successfully refreshed with sirius'
                      );
                    },
                    (sessionError) => {
                      this.log(
                        LOG_WARN,
                        'Failed to refresh session with sirius',
                        sessionError
                      );
                    }
                  );

                  if (this.authWorker) {
                    this.startTokenMonitoring(token);
                  }
                },
                error: (error) => {
                  this.log(LOG_ERROR, 'Error retrieving user data', error);
                  this.handleRefreshError(retryAttempt, error, false);
                },
              });
            } else {
              this.log(
                LOG_ERROR,
                'Token refresh response did not include an access token'
              );
              this.handleRefreshError(
                retryAttempt,
                new Error('Missing access token'),
                false
              );
            }
          },
          error: (error) => {
            this.log(
              LOG_ERROR,
              `Refresh failed after ${Date.now() - refreshStartTime}ms`,
              error
            );

            // Check if error is due to auth server being down...
            const isServerDown = this.isServerDownError(error);
            // If the server is down, let's keep existing token and retry
            // If invalid refresh token or other error, clear state and re-authorize
            if (isServerDown) {
              this.handleRefreshError(retryAttempt, error, true);
            } else {
              this.log(LOG_WARN, 'No valid refresh token, triggering re-auth');
              this.isRefreshing = false;
              this.cachedState = {};
              this.store.dispatch(
                PLAuthError({ error: 'Invalid refresh token' })
              );
              this.oidcSecurityService.authorize();
            }
          },
        });
      },
      error: (error) => {
        this.log(LOG_ERROR, 'Error fetching refresh token', error);
        this.isRefreshing = false;
        this.handleRefreshError(retryAttempt, error, false);
      },
    });
  }

  private handleLogoutMessage(message: LogoutMessage) {
    this.log(LOG_INFO, `Received logout message from tab ${message.tabId}`);

    this.oidcSecurityService.logoffLocal(); // Ensure that the OIDC client clears its storage!

    if (this.authWorker) {
      this.authWorker.postMessage({ type: 'STOP_TOKEN_MONITORING' });
    }

    this.cachedState = {};

    // Notify store about logout
    this.store.dispatch(PLAuthError({ error: 'User logged out' }));
  }

  private isServerDownError(error: any): boolean {
    // Check direct status first
    if (error?.status === 502 || error?.status === 503) {
      return true;
    }

    // Check wrapped error object (byproduct of `angular-auth-oidc-client` wrapping errors)
    if (error?.error?.status === 502 || error?.error?.status === 503) {
      return true;
    }

    // Check if error message contains relevant text
    const errorMsg = error?.message?.toLowerCase() || '';
    return errorMsg.includes('code request') && errorMsg.includes('sirius');
  }

  private handleRefreshError(
    retryAttempt: number,
    error: any,
    isRetryable: boolean
  ) {
    const maxRetries = isRetryable ? 5 : 3;
    const baseDelay = isRetryable ? 2000 : 1000;
    const backoffMs = Math.min(baseDelay * 2 ** retryAttempt, 30000); // Max 30s

    if (retryAttempt < maxRetries) {
      this.log(
        LOG_WARN,
        `Scheduling retry ${retryAttempt + 1} in ${backoffMs}ms ${
          isRetryable ? '(server down)' : ''
        }`
      );

      setTimeout(() => {
        this.handleTokenRefresh(retryAttempt + 1);
      }, backoffMs);
    } else {
      this.log(LOG_ERROR, 'Max retries reached, notifying error');

      // Clear retry state
      this.isRefreshing = false;
      this.store.dispatch(
        PLAuthError({
          error: isRetryable
            ? 'Auth server unavailable after max retries'
            : error || 'Max refresh retries exceeded',
        })
      );
      this.notifyRefreshDone('', '', '');

      // If this was a server down scenario, try to restart monitoring with existing token
      if (isRetryable) {
        // Schedule a final monitoring cycle
        this.oidcSecurityService
          .getAccessToken()
          .pipe(take(1))
          .subscribe({
            next: (token) => {
              if (token) {
                this.startTokenMonitoring(token);
              } else {
                this.log(
                  LOG_WARN,
                  'No token available for monitoring after server down'
                );
              }
            },
            error: (tokenError) => {
              this.log(
                LOG_ERROR,
                'Failed to get token during final monitoring: ',
                tokenError
              );
            },
          });
      } else {
        // For other errors, trigger re-auth
        this.oidcSecurityService.authorize();
      }
    }
  }

  /**
   * Refreshes the server-side session via the RefreshSession handler in sirius
   * This uses the /session/refresh endpoint to renew the session token
   * without requiring a full token refresh
   * @param token The access token to use for session refresh
   * @returns A promise that resolves when the session has been refreshed
   */
  private refreshSession(token: string): Promise<void> {
    const url = `${environment.sirius_url}/auth/session/refresh`;

    return firstValueFrom(
      this.http.post<{ message: string }>(
        url,
        {},
        {
          headers: {
            Authorization: `JWT ${token}`,
          },
          withCredentials: true,
        }
      )
    ).then(() => {});
  }

  private getUserAndCheckAuth(oidcState: any) {
    const oidcStateAsJSON = JSON.parse(oidcState) as OIDCState;
    const userData = oidcStateAsJSON.userData;
    const userUUID = userData.auth_db_user_uuid;

    const user = {
      uuid: userUUID,
      username: userData.auth_db_user_username,
      email: userData.email,
      display_name: userData.name,
      first_name: userData.given_name,
      last_name: userData.family_name,
      is_active: userData.email_verified,
      is_staff: userData.role !== 'provider',
    };

    this.oidcSecurityService
      .getAccessToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          this.authService.getUser().subscribe({
            next: (result) => {
              this.store.dispatch(
                PLAuthSuccess({
                  user: { ...user, groups: result.groups },
                  token,
                })
              );
            },
            error: (userError) => {
              this.log(LOG_ERROR, 'Error fetching user details:', userError);
            },
          });

          // Perform auth check after dispatching user details
          this.oidcSecurityService
            .checkAuth()
            .pipe(take(1))
            .subscribe({
              next: (result) => {
                this.log(LOG_INFO, 'Auth check after state sync:', result);
              },
              error: (authError) => {
                this.log(LOG_ERROR, 'Error during auth check:', authError);
              },
            });
        },
        error: (tokenError) => {
          this.log(LOG_ERROR, 'Error fetching access token:', tokenError);
        },
      });
  }

  ngOnDestroy() {
    this.authWorker?.terminate();
    this.authChannel.close();
    window.removeEventListener('storage', this.handleLeaderChange.bind(this));
    window.removeEventListener('beforeunload', this.cleanupOnUnload.bind(this));
  }
}
